import clsx from 'clsx';
import React, { useRef, useState } from 'react';
import MultiCarousel from 'react-multi-carousel';
import styles from 'components/ui/ContentBlocks/Carousel.module.scss';
import CustomSliderArrow from 'components/ui/CustomSliderArrow';
import Image from 'components/ui/Image';
import { useVerticalSwipePreventer } from 'hooks/useVerticalSwipePreventer';
import type { Image as ImageType } from 'types/Utilities';

type CarouselProps = {
  expand?: boolean;
  overlayClick?: boolean;
  slides: ImageType[];
  startSlide?: number;
  update: (current?: number) => void;
};

const Carousel = ({
  expand,
  overlayClick,
  slides,
  update,
}: CarouselProps): JSX.Element => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const sliderRef = useRef<MultiCarousel | null>(null);
  const [curSlide, setCurSlide] = useState(0);
  useVerticalSwipePreventer(containerRef);

  return (
    <div className="relative" ref={containerRef}>
      <MultiCarousel
        ref={sliderRef}
        // ssr only works with deviceType set to a value defined in responsive
        ssr
        deviceType="all"
        responsive={{
          all: {
            breakpoint: { max: 4000, min: 0 },
            items: 1,
          },
        }}
        customLeftArrow={
          <CustomSliderArrow
            direction="left"
            className={styles.carouselArrowLeft}
          />
        }
        customRightArrow={
          <CustomSliderArrow
            direction="right"
            className={styles.carouselArrowRight}
          />
        }
        containerClass={`relative flex items-center overflow-hidden`}
        // flex-grow is needed for when JS is disabled
        sliderClass="relative flex-grow flex flex-row list-none p-0 m-0"
        itemClass="shrink-0 self-center carousel-item"
        infinite
        beforeChange={(nextSlide, { currentSlide }) => {
          let index;
          if (nextSlide > currentSlide) {
            index = curSlide + 1;
          } else {
            index = curSlide - 1;
          }

          if (index === slides.length) {
            update(0);
            setCurSlide(0);
            return;
          }

          if (index === -1) {
            update(slides.length - 1);
            setCurSlide(slides.length - 1);
            return;
          }

          update(index);
          setCurSlide(index);
        }}
      >
        {slides.map((slide, idx) => {
          return (
            <div
              className="relative flex justify-center shrink-0 mx-auto h-64 lg:h-auto lg:w-11/12 items-center lg:my-4"
              key={idx}
            >
              <Image
                className={clsx(
                  expand ? 'rounded-md object-contain' : 'object-cover',
                )}
                src={slide.url}
                alt={slide.description || slide.title || 'carousel image'}
                contentful
                fill
              />

              {overlayClick && (
                <>
                  <div
                    className="absolute top-0 left-0 h-full w-2/4 cursor-pointer"
                    onClick={() => {
                      sliderRef.current?.previous(0);
                    }}
                  />
                  <div
                    className="absolute top-0 right-0 h-full w-2/4 cursor-pointer"
                    onClick={() => {
                      sliderRef.current?.next(0);
                    }}
                  />
                </>
              )}
            </div>
          );
        })}
      </MultiCarousel>
    </div>
  );
};

type CarouselContainerProps = {
  blockId?: string;
  slides?: ImageType[];
  className?: string;
};

const CarouselContainer = ({
  blockId,
  slides,
  className,
}: CarouselContainerProps): JSX.Element | null => {
  const [, setCurrentSlide] = useState(0);
  const nodeRef = React.createRef<HTMLDivElement>();

  if (!slides?.length) {
    return null;
  }

  return (
    <div
      id={blockId}
      className={clsx('max-w-screen-xl mx-auto lg:px-4', className)}
    >
      <div ref={nodeRef}>
        <Carousel
          update={(current) => {
            setCurrentSlide(current as number);
          }}
          slides={slides.filter(Boolean)}
        />
      </div>
    </div>
  );
};

export default CarouselContainer;
